import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, ThumbsUp, ThumbsDown } from "lucide-react";
import { api } from "@/lib/brands";

const CID_KEY = "genie_chat_cid";
const NAME_KEY = "genie_chat_name";
const EMAIL_KEY = "genie_chat_email";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [cid, setCid] = useState(localStorage.getItem(CID_KEY) || null);
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  const [email, setEmail] = useState(localStorage.getItem(EMAIL_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [online, setOnline] = useState(false);
  const [specTyping, setSpecTyping] = useState(false);
  const [ended, setEnded] = useState(false);
  const [endPrompt, setEndPrompt] = useState(false);
  const [endEmail, setEndEmail] = useState("");
  const [endNote, setEndNote] = useState("");
  const [rated, setRated] = useState(false);
  const [rateComment, setRateComment] = useState("");
  const [rateValue, setRateValue] = useState(null);
  const sinceRef = useRef(null);
  const endRef = useRef(null);
  const typingSentRef = useRef(0);

  // Poll for new messages
  useEffect(() => {
    if (!open || !cid) return;
    let active = true;
    async function poll() {
      try {
        const { data } = await api.get(`/chat/${cid}/messages`, {
          params: sinceRef.current ? { since: sinceRef.current } : {},
        });
        if (active && data.length) {
          setMessages((m) => [...m, ...data]);
          sinceRef.current = data[data.length - 1].created_at;
        }
      } catch { /* ignore */ }
      if (active) setTimeout(poll, 3000);
    }
    poll();
    return () => { active = false; };
  }, [open, cid]);

  // Poll specialist online / typing / chat status
  useEffect(() => {
    if (!open || !cid) return;
    let active = true;
    async function pollStatus() {
      try {
        const { data } = await api.get(`/chat/${cid}/status`);
        if (active) {
          setOnline(!!data.specialist_online);
          setSpecTyping(!!data.specialist_typing);
          setEnded(data.status === "ended");
        }
      } catch { /* ignore */ }
      if (active) setTimeout(pollStatus, 3000);
    }
    pollStatus();
    return () => { active = false; };
  }, [open, cid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, specTyping]);

  async function start(e) {
    e.preventDefault();
    if (!name.trim()) return;
    const { data } = await api.post("/chat/start", { name, email: email.trim() || null });
    localStorage.setItem(CID_KEY, data.conversation_id);
    localStorage.setItem(NAME_KEY, name);
    if (email.trim()) localStorage.setItem(EMAIL_KEY, email.trim());
    setCid(data.conversation_id);
  }

  function onType(e) {
    setText(e.target.value);
    const now = Date.now();
    if (cid && now - typingSentRef.current > 2000) {
      typingSentRef.current = now;
      api.post(`/chat/${cid}/typing`).catch(() => {});
    }
  }

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post(`/chat/${cid}/message`, { sender_name: name, text });
    setMessages((m) => [...m, data]);
    sinceRef.current = data.created_at;
    setText("");
  }

  function requestEnd() {
    if (email.trim()) {
      confirmEnd(email.trim());
    } else {
      setEndPrompt(true);
    }
  }

  async function confirmEnd(withEmail) {
    try {
      const { data } = await api.post(`/chat/${cid}/end`, { email: withEmail || null });
      if (withEmail) localStorage.setItem(EMAIL_KEY, withEmail);
      setEnded(true);
      setEndPrompt(false);
      setEndNote(data.emailed ? "Chat ended — a transcript is on its way to your inbox." : "Chat ended. Thanks for reaching out.");
    } catch {
      setEndNote("Chat ended.");
      setEnded(true);
      setEndPrompt(false);
    }
  }

  function reset() {
    localStorage.removeItem(CID_KEY);
    setCid(null);
    setMessages([]);
    sinceRef.current = null;
    setEnded(false);
    setEndNote("");
    setText("");
    setRated(false);
    setRateValue(null);
    setRateComment("");
  }

  async function submitRating(value) {
    setRateValue(value);
    try {
      await api.post(`/chat/${cid}/rate`, { value, comment: rateComment.trim() || null });
    } catch { /* ignore */ }
    setRated(true);
  }

  return (
    <>
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label="Live chat with a specialist"
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-paper shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-50 flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col border-2 border-ink bg-paper"
        >
          <div className="border-b border-hair bg-ink px-4 py-3 text-paper">
            <div className="flex items-center justify-between">
              <div className="font-serif text-base">Chat with a specialist</div>
              {cid && (
                <span
                  data-testid="chat-online-status"
                  className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-paper/80"
                >
                  <span className={`h-2 w-2 rounded-full ${online ? "bg-green-400" : "bg-paper/40"}`} />
                  {online ? "Online" : "Away"}
                </span>
              )}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-paper/70">
              Independent · we never ask for passwords
            </div>
          </div>

          {!cid ? (
            <form onSubmit={start} className="flex flex-1 flex-col justify-center gap-3 p-5">
              <p className="text-sm text-muted">Enter your details to start chatting.</p>
              <input
                data-testid="chat-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="border-2 border-hair bg-transparent px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <input
                data-testid="chat-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional — for a transcript)"
                className="border-2 border-hair bg-transparent px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <button data-testid="chat-start-button" className="bg-accent px-4 py-2.5 font-mono text-xs uppercase tracking-widest text-paper hover:opacity-90">
                Start chat
              </button>
            </form>
          ) : (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto p-3" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="mt-4 text-center font-mono text-[11px] text-muted">
                    Say hi — a specialist will reply here.
                  </p>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-3 py-2 text-sm ${m.sender === "customer" ? "bg-ink text-paper" : "border border-hair bg-transparent text-ink"}`}>
                      {m.sender === "specialist" && (
                        <div className="font-mono text-[9px] uppercase tracking-widest text-accent">{m.sender_name}</div>
                      )}
                      {m.text}
                    </div>
                  </div>
                ))}
                {specTyping && !ended && (
                  <div className="flex justify-start" data-testid="chat-spec-typing">
                    <div className="border border-hair px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted">
                      Specialist is typing…
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {ended ? (
                <div className="border-t border-hair p-4 text-center" data-testid="chat-ended-note">
                  <p className="text-sm text-ink">{endNote || "This chat has ended."}</p>
                  {!rated ? (
                    <div className="mt-4" data-testid="chat-rate">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">How did we do?</p>
                      <div className="mt-2 flex justify-center gap-3">
                        <button
                          onClick={() => submitRating("up")}
                          data-testid="chat-rate-up"
                          className="flex h-10 w-10 items-center justify-center border-2 border-ink text-ink transition-colors hover:border-accent hover:text-accent"
                          aria-label="Thumbs up"
                        >
                          <ThumbsUp className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => submitRating("down")}
                          data-testid="chat-rate-down"
                          className="flex h-10 w-10 items-center justify-center border-2 border-ink text-ink transition-colors hover:border-accent hover:text-accent"
                          aria-label="Thumbs down"
                        >
                          <ThumbsDown className="h-5 w-5" />
                        </button>
                      </div>
                      <input
                        data-testid="chat-rate-comment"
                        value={rateComment}
                        onChange={(e) => setRateComment(e.target.value)}
                        placeholder="Add a comment (optional)"
                        className="mt-3 w-full border-2 border-hair bg-transparent px-3 py-2 text-sm focus:border-accent focus:outline-none"
                      />
                    </div>
                  ) : (
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-accent" data-testid="chat-rate-thanks">
                      {rateValue === "up" ? "Thanks for the kind words!" : "Thanks — we'll do better."}
                    </p>
                  )}
                  <a
                    href="/chat?followup=1"
                    data-testid="chat-widget-followup"
                    className="mt-4 block bg-accent px-4 py-2.5 text-center font-mono text-[10px] uppercase tracking-widest text-paper hover:opacity-90"
                  >
                    Need more help? Book a follow-up · 30% off
                  </a>
                  <button
                    onClick={reset}
                    data-testid="chat-new-button"
                    className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted underline hover:text-accent"
                  >
                    Start a new chat
                  </button>
                </div>
              ) : endPrompt ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); confirmEnd(endEmail.trim()); }}
                  className="border-t border-hair p-3"
                  data-testid="chat-end-prompt"
                >
                  <p className="mb-2 text-xs text-muted">Want a copy of this chat? Add your email (optional).</p>
                  <div className="flex gap-2">
                    <input
                      data-testid="chat-end-email-input"
                      type="email"
                      value={endEmail}
                      onChange={(e) => setEndEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="flex-1 border-2 border-hair bg-transparent px-3 py-2 text-sm focus:border-accent focus:outline-none"
                    />
                    <button data-testid="chat-end-confirm" className="bg-accent px-3 font-mono text-[10px] uppercase tracking-widest text-paper hover:opacity-90">
                      End
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => confirmEnd("")}
                    data-testid="chat-end-skip"
                    className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted underline hover:text-accent"
                  >
                    End without transcript
                  </button>
                </form>
              ) : (
                <>
                  <form onSubmit={send} className="flex gap-2 border-t border-hair p-3">
                    <input
                      data-testid="chat-text-input"
                      value={text}
                      onChange={onType}
                      placeholder="Type a message…"
                      className="flex-1 border-2 border-hair bg-transparent px-3 py-2 text-sm focus:border-accent focus:outline-none"
                    />
                    <button data-testid="chat-send-button" className="bg-accent px-3 text-paper hover:opacity-90">
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                  <button
                    onClick={requestEnd}
                    data-testid="chat-end-button"
                    className="border-t border-hair py-2 font-mono text-[10px] uppercase tracking-widest text-muted hover:text-accent"
                  >
                    End chat
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
